import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  Shield, CheckCircle2, XCircle, Lock, UserPlus,
  Key, Eye, EyeOff, Power, ShieldOff, Copy, Save, Clock,
  AlertCircle, RefreshCw,
} from "lucide-react";

function formatDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function formatDateShort(iso) {
  try { return new Date(iso).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function suggestUsername(c) {
  if (!c?.first_name || !c?.last_name) return "";
  return `${c.first_name[0].toLowerCase()}${c.last_name.toLowerCase().replace(/\s/g, "")}`;
}

export default function PortalAdminModule() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);

  const [selectedClientId, setSelectedClientId] = useState(() => searchParams.get("client_id") || "");
  const [config, setConfig] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mustChange, setMustChange] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const navigate = useNavigate();

  const loadOverview = useCallback(async () => {
    setLoadingOverview(true);
    try { const r = await api.get("/portal/admin/overview"); setOverview(r.data); }
    catch { setOverview(null); }
    finally { setLoadingOverview(false); }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const selectedRow = useMemo(() => {
    const rows = overview?.rows || [];
    return rows.find(r => r.id === selectedClientId) || null;
  }, [overview, selectedClientId]);

  const rows = overview?.rows || [];

  // Batch #14.75 — Fusion avec la liste Comptes clients : si aucun client n'est
  // sélectionné via ?client_id=, on redirige vers la liste qui sert maintenant de picker.
  useEffect(() => {
    if (!selectedClientId && !loadingOverview && overview) {
      navigate("/admin/portail-clients", { replace: true });
    }
  }, [selectedClientId, loadingOverview, overview, navigate]);

  // Load selected client portal config
  const loadConfig = useCallback(async (id) => {
    if (!id) { setConfig(null); return; }
    try {
      const r = await api.get(`/clients/${id}/portal-config`);
      setConfig(r.data);
      setUsername(r.data.portal_username || suggestUsername(r.data));
      setPassword("");
      setTempPassword("");
      setMustChange(true);
      // Login history
      try {
        const sec = await api.get("/security/dashboard");
        const uname = r.data.portal_username;
        const recent = uname
          ? (sec.data.recent_logins || [])
              .filter(l => l.login_type === "portal" && (l.identifier || "").endsWith(`:${uname}`))
              .slice(0, 20)
          : [];
        setLoginHistory(recent);
      } catch { setLoginHistory([]); }
    } catch {
      toast.error("Client introuvable");
      setConfig(null);
    }
  }, []);

  useEffect(() => { loadConfig(selectedClientId); }, [selectedClientId, loadConfig]);

  const selectClient = (id) => {
    setSelectedClientId(id);
    if (id) setSearchParams({ client_id: id });
    else setSearchParams({});
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let p = ""; for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setPassword(p); setShowPassword(true);
    toast.success("Mot de passe genere");
  };

  const handleSaveCredentials = async () => {
    if (!username || username.length < 3) { toast.error("Identifiant 3 caracteres min"); return; }
    if (!password || password.length < 8) { toast.error("Mot de passe 8 caracteres min"); return; }
    setSaving(true);
    try {
      await api.post(`/clients/${selectedClientId}/portal-credentials`, {
        portal_username: username,
        portal_password: password,
        must_change_password: mustChange,
      });
      toast.success(config?.portal_username ? "Identifiants mis a jour" : "Acces cree");
      setTempPassword(password);
      setPassword("");
      await loadOverview();
      await loadConfig(selectedClientId);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleToggle = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clients/${selectedClientId}/portal-toggle`);
      toast.success(r.data.portal_enabled ? "Portail active" : "Portail desactive");
      await loadOverview();
      await loadConfig(selectedClientId);
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const handleReset = async () => {
    if (!window.confirm("Reinitialiser le mot de passe avec un MDP temporaire ?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/clients/${selectedClientId}/portal-reset-password`);
      setTempPassword(r.data.temporary_password);
      try { await navigator.clipboard.writeText(r.data.temporary_password); } catch {}
      toast.success("MDP temporaire genere et copie");
      await loadOverview();
      await loadConfig(selectedClientId);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const handleUnlock = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${selectedClientId}/portal-unlock`);
      toast.success("Compte debloque");
      await loadOverview();
      await loadConfig(selectedClientId);
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const handleForceChange = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${selectedClientId}/portal-force-password-change`);
      toast.success("Changement force au prochain login");
      await loadOverview();
      await loadConfig(selectedClientId);
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/portal`;
    await navigator.clipboard.writeText(`Portail : ${url}\nIdentifiant : ${config?.portal_username || ""}`);
    toast.success("Lien copie");
  };

  const configured = !!config?.portal_username;
  const enabled = !!config?.portal_enabled;

  return (
    <div className="max-w-4xl mx-auto" data-testid="portal-admin-module">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-600" /> Gestion du portail client
          </h1>
        </div>
        <button onClick={loadOverview}
          className="border px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 hover:bg-gray-50"
          data-testid="portal-refresh">
          <RefreshCw className="w-3 h-3" /> Actualiser
        </button>
      </div>

      {/* Client loading */}
      {selectedClientId && !config && (
        <div className="bg-white border rounded-xl px-4 py-10 text-center" data-testid="portal-detail-loading">
          <div className="w-5 h-5 border-2 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto" />
        </div>
      )}

      {/* Selected client — full controls */}
      {config && (
        <div className="space-y-4" data-testid="portal-selected-panel">
          {/* Status header */}
          <div className="bg-white border rounded-xl p-4 flex items-center gap-4 flex-wrap">
            <div className="w-12 h-12 rounded-full bg-green-50 text-green-600 flex items-center justify-center text-lg font-bold flex-shrink-0">
              {(config.first_name?.[0] || "").toUpperCase()}{(config.last_name?.[0] || "").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold">{config.first_name} {config.last_name}</h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {selectedRow?.is_locked ? (
                  <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full flex items-center gap-1"><Lock className="w-3 h-3" /> Verrouille</span>
                ) : !configured ? (
                  <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full flex items-center gap-1"><UserPlus className="w-3 h-3" /> Non configure</span>
                ) : enabled ? (
                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Actif</span>
                ) : (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full flex items-center gap-1"><XCircle className="w-3 h-3" /> Inactif</span>
                )}
                {config.must_change_password && enabled && (
                  <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">MDP a changer</span>
                )}
                {config.portal_username && <span className="text-xs text-gray-500">@{config.portal_username}</span>}
              </div>
            </div>
          </div>

          {/* Activate checkbox + main status controls */}
          <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="portal-activate-card">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Power className="w-4 h-4 text-green-600" /> Etat de l'acces
            </h3>

            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${enabled ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
              <input type="checkbox" checked={enabled}
                disabled={!configured || busy}
                onChange={handleToggle}
                className="w-4 h-4 rounded text-green-600"
                data-testid="portal-enabled-toggle" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">
                  {enabled ? "Portail actif" : configured ? "Portail inactif" : "Portail non configure"}
                </p>
                <p className="text-xs text-gray-500">
                  {enabled
                    ? "Le client peut se connecter a son portail."
                    : configured
                      ? "Le client ne peut pas se connecter. Cochez pour reactiver."
                      : "Configurez d'abord un identifiant et un mot de passe ci-dessous."}
                </p>
              </div>
            </label>

            {selectedRow?.is_locked && (
              <button onClick={handleUnlock} disabled={busy}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-red-700"
                data-testid="portal-unlock-btn">
                <ShieldOff className="w-4 h-4" /> Debloquer le compte
              </button>
            )}
          </div>

          {/* Credentials card */}
          <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="portal-credentials-card">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Key className="w-4 h-4 text-green-600" />
              {configured ? "Modifier les identifiants" : "Creer le premier acces"}
            </h3>

            {tempPassword && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm" data-testid="temp-password-box">
                <p className="text-amber-800 font-semibold mb-1">Mot de passe {configured ? "reinitialise" : "defini"}</p>
                <p className="font-mono bg-white border rounded px-2 py-1 inline-block select-all">{tempPassword}</p>
                <p className="text-xs text-amber-600 mt-2">Copie dans le presse-papier. Transmettez-le au client de facon securitaire.</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Identifiant (username) *</label>
                <input type="text" value={username}
                  onChange={e => setUsername(e.target.value.toLowerCase().replace(/\s/g, ""))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="ex. jdupont"
                  data-testid="portal-username-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {configured ? "Nouveau mot de passe" : "Premier mot de passe"} (min 8) *
                </label>
                <div className="flex gap-1">
                  <div className="relative flex-1">
                    <input type={showPassword ? "text" : "password"} value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm pr-9" placeholder="********"
                      data-testid="portal-password-input" />
                    <button onClick={() => setShowPassword(!showPassword)}
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <button onClick={generatePassword} type="button"
                    className="border px-2 py-1 rounded-lg text-xs flex items-center gap-1 hover:bg-gray-50"
                    title="Generer un mot de passe"
                    data-testid="portal-generate-password">
                    <Key className="w-3 h-3" /> Gen
                  </button>
                </div>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={mustChange} onChange={e => setMustChange(e.target.checked)}
                data-testid="portal-must-change-checkbox" />
              <span>Exiger le changement au prochain login (recommande)</span>
            </label>

            <div className="flex gap-2 flex-wrap">
              <button onClick={handleSaveCredentials} disabled={saving}
                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="portal-save-credentials">
                <Save className="w-4 h-4" />
                {saving ? "..." : configured ? "Mettre a jour" : "Creer l'acces"}
              </button>
              {configured && enabled && (
                <>
                  <button onClick={handleReset} disabled={busy}
                    className="border border-amber-200 text-amber-600 px-3 py-2 rounded-lg text-sm hover:bg-amber-50 flex items-center gap-2"
                    data-testid="portal-reset-password">
                    <Key className="w-3.5 h-3.5" /> Reinitialiser MDP
                  </button>
                  {!config.must_change_password && (
                    <button onClick={handleForceChange} disabled={busy}
                      className="border px-3 py-2 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2"
                      data-testid="portal-force-change">
                      <AlertCircle className="w-3.5 h-3.5" /> Forcer changement
                    </button>
                  )}
                  <button onClick={handleCopyLink}
                    className="border px-3 py-2 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2"
                    data-testid="portal-copy-link">
                    <Copy className="w-3.5 h-3.5" /> Copier lien + identifiant
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Activity card */}
          <div className="bg-white border rounded-xl p-4" data-testid="portal-activity-card">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-blue-600" /> Suivi des acces
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500">Dernier acces</p>
                <p className="font-medium">
                  {selectedRow?.last_login
                    ? formatDate(selectedRow.last_login)
                    : <span className="text-gray-400">Jamais connecte</span>}
                </p>
                {selectedRow?.last_login_ip && <p className="text-xs text-gray-400">IP : {selectedRow.last_login_ip}</p>}
                {selectedRow?.failed_attempts > 0 && (
                  <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2">
                    <p className="text-xs text-red-600">{selectedRow.failed_attempts} echec(s) recent(s)</p>
                    {selectedRow.is_locked && <p className="text-[10px] text-red-500">Compte actuellement verrouille</p>}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Historique (20 derniers)</p>
                {loginHistory.length === 0 ? (
                  <p className="text-xs text-gray-400">Aucun historique</p>
                ) : (
                  <div className="max-h-[160px] overflow-y-auto border rounded-lg divide-y">
                    {loginHistory.map((l, i) => (
                      <div key={i} className="px-2 py-1.5 text-xs flex items-center gap-1">
                        {l.success
                          ? <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                          : <XCircle className="w-3 h-3 text-red-500 flex-shrink-0" />}
                        <span className="text-gray-500 flex-shrink-0">{formatDateShort(l.timestamp)}</span>
                        <span className="text-gray-400 truncate ml-auto text-[10px]">{l.ip || ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
